import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { isAdminUser } from '@core/auth/role.utils';
import { AuthStore } from '@core/stores/auth.store';
import { OrganisationTreeItem } from '@shared/components/organisation-tree/organisation-tree.model';
import { MemberOrganisation } from '@shared/models/organisation.interface';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';
import { OrganisationsApiService } from './organisations-api.service';

const RECENT_ORGANISATIONS_LIMIT = 3;

interface SavedSelection {
  organisationId: string | null;
  recentOrganisationIds: string[];
}

interface TenantFilterState {
  /** Organisation picked in the top bar; null only until the organisations have loaded. */
  organisationId: string | null;
  /** The whole tenant hierarchy — loaded for staff and client admins only. */
  organisationTree: OrganisationTreeItem[];
  /** A client user's own organisations — the only ones they can pick. */
  memberOrganisations: MemberOrganisation[];
  /** Last organisations picked in the top bar, most recent first. */
  recentOrganisationIds: string[];
  isLoadingOrganisations: boolean;
  /** Where the selection is kept between visits, per user and tenant. */
  storageKey: string | null;
}

const initialState: TenantFilterState = {
  organisationId: null,
  organisationTree: [],
  memberOrganisations: [],
  recentOrganisationIds: [],
  isLoadingOrganisations: false,
  storageKey: null,
};

const readSelection = (key: string): SavedSelection | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as SavedSelection) : null;
  } catch {
    return null;
  }
};

/** The filters of the tenant top bar, shared with every page under `/tenant/:tenantId`. */
export const TenantFilterStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(() => {
    const authStore = inject(AuthStore);
    return {
      /** Client users only see their own organisations, never the tenant tree. */
      browsesTree: computed(() => isAdminUser(authStore.user())),
    };
  }),
  withMethods((store) => {
    const organisationsApi = inject(OrganisationsApiService);
    const authStore = inject(AuthStore);

    const saveSelection = () => {
      const key = store.storageKey();
      if (!key) return;
      const selection: SavedSelection = {
        organisationId: store.organisationId(),
        recentOrganisationIds: store.recentOrganisationIds(),
      };
      localStorage.setItem(key, JSON.stringify(selection));
    };

    /** Restores the saved selection when it still exists, else falls back to `fallbackId`. */
    const restoreSelection = (availableIds: Set<string>, fallbackId: string | null) => {
      const key = store.storageKey();
      const saved = key ? readSelection(key) : null;
      const savedId = saved?.organisationId;
      patchState(store, {
        recentOrganisationIds: (saved?.recentOrganisationIds ?? []).filter((id) =>
          availableIds.has(id),
        ),
      });
      // Keep whatever the user already picked during this visit.
      const currentId = store.organisationId();
      if (currentId && availableIds.has(currentId)) return;
      patchState(store, {
        organisationId: savedId && availableIds.has(savedId) ? savedId : fallbackId,
      });
    };

    return {
      setOrganisation(organisationId: string | null) {
        if (!organisationId) {
          patchState(store, { organisationId: null });
          return;
        }
        patchState(store, {
          organisationId,
          recentOrganisationIds: [
            organisationId,
            ...store.recentOrganisationIds().filter((id) => id !== organisationId),
          ].slice(0, RECENT_ORGANISATIONS_LIMIT),
        });
        saveSelection();
      },

      reset() {
        patchState(store, { ...initialState });
      },

      loadOrganisations(tenantId: string | null): Observable<unknown> {
        const userId = authStore.user()?.id;
        patchState(store, {
          isLoadingOrganisations: true,
          storageKey: userId && tenantId ? `prev.organisation.${userId}.${tenantId}` : null,
        });

        const request$: Observable<unknown> = store.browsesTree()
          ? organisationsApi.findTree().pipe(
              tap((organisationTree) => {
                patchState(store, { organisationTree });
                // Default to the top-level organisation rather than "all organisations".
                const root = organisationTree.find((organisation) => !organisation.parentId);
                restoreSelection(
                  new Set(organisationTree.map((organisation) => organisation.id)),
                  root?.id ?? null,
                );
              }),
            )
          : organisationsApi.findMine().pipe(
              tap((memberOrganisations) => {
                const sorted = [...memberOrganisations].sort((a, b) =>
                  [...a.path, a.name].join('/').localeCompare([...b.path, b.name].join('/')),
                );
                patchState(store, { memberOrganisations: sorted });
                restoreSelection(
                  new Set(sorted.map((organisation) => organisation.id)),
                  sorted[0]?.id ?? null,
                );
              }),
            );

        return request$.pipe(
          catchError(() => EMPTY),
          finalize(() => patchState(store, { isLoadingOrganisations: false })),
        );
      },
    };
  }),
);
